/**
 * Platform-aware token storage adapter.
 *
 * Web: tokens live in `localStorage` exactly as before (key names preserved
 * so existing browser sessions keep working through deploy).
 *
 * Capacitor (iOS / Android):
 *  - Access token: in-memory only (module-private variable, not on `window`),
 *    so it never persists to a file the OS can leak via backup or jailbreak.
 *  - Refresh token: `@capacitor/preferences`, which on iOS wraps the system
 *    Keychain and on Android uses EncryptedSharedPreferences when available.
 *
 * The interface is Promise-returning on both platforms for symmetry; the
 * synchronous `getAccessSync` escape hatch exists so the request-header path
 * (sync) can still read the access token cheaply.
 */
import { native } from "../native/index";
import type { TokenStore } from "@shared/lib/api/client";

export type { TokenStore };

const ACCESS_TOKEN_KEY = "auth_access_token";
const REFRESH_TOKEN_KEY = "auth_refresh_token";
const NATIVE_MIGRATION_MARKER_KEY = "auth_migrated_to_native_v1";

// ── Web implementation ───────────────────────────────────────────────

const memoryFallback = new Map<string, string>();

function safeStorage(): Pick<Storage, "getItem" | "setItem" | "removeItem"> {
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch {
    // Some browser contexts expose storage but throw on access.
  }
  return {
    getItem: (key: string) => memoryFallback.get(key) ?? null,
    setItem: (key: string, value: string) => {
      memoryFallback.set(key, value);
    },
    removeItem: (key: string) => {
      memoryFallback.delete(key);
    },
  };
}

export class WebTokenStore implements TokenStore {
  async getAccess(): Promise<string | null> {
    return safeStorage().getItem(ACCESS_TOKEN_KEY);
  }

  getAccessSync(): string | null {
    return safeStorage().getItem(ACCESS_TOKEN_KEY);
  }

  async setAccess(token: string | null): Promise<void> {
    if (token === null) {
      safeStorage().removeItem(ACCESS_TOKEN_KEY);
    } else {
      safeStorage().setItem(ACCESS_TOKEN_KEY, token);
    }
  }

  async getRefresh(): Promise<string | null> {
    return safeStorage().getItem(REFRESH_TOKEN_KEY);
  }

  async setRefresh(token: string | null): Promise<void> {
    if (token === null) {
      safeStorage().removeItem(REFRESH_TOKEN_KEY);
    } else {
      safeStorage().setItem(REFRESH_TOKEN_KEY, token);
    }
  }

  async clear(): Promise<void> {
    safeStorage().removeItem(ACCESS_TOKEN_KEY);
    safeStorage().removeItem(REFRESH_TOKEN_KEY);
  }

  async migrateFromWebStorageIfNeeded(): Promise<void> {
    // No-op on web.
  }
}

// ── Native implementation ────────────────────────────────────────────

/**
 * Module-private in-memory cache for the native access token.
 * Deliberately NOT attached to `window` / `globalThis` so inspector tooling
 * inside a compromised WebView cannot read it via reference.
 */
let nativeAccessTokenMemory: string | null = null;

export class NativeTokenStore implements TokenStore {
  async getAccess(): Promise<string | null> {
    return nativeAccessTokenMemory;
  }

  getAccessSync(): string | null {
    return nativeAccessTokenMemory;
  }

  async setAccess(token: string | null): Promise<void> {
    nativeAccessTokenMemory = token;
    // Never persisted.
  }

  async getRefresh(): Promise<string | null> {
    const result = await native.preferences.get({ key: REFRESH_TOKEN_KEY });
    return result.value ?? null;
  }

  async setRefresh(token: string | null): Promise<void> {
    if (token === null) {
      await native.preferences.remove({ key: REFRESH_TOKEN_KEY });
    } else {
      await native.preferences.set({ key: REFRESH_TOKEN_KEY, value: token });
    }
  }

  async clear(): Promise<void> {
    nativeAccessTokenMemory = null;
    await native.preferences.remove({ key: REFRESH_TOKEN_KEY });
  }

  async migrateFromWebStorageIfNeeded(): Promise<void> {
    const marker = await native.preferences.get({
      key: NATIVE_MIGRATION_MARKER_KEY,
    });
    if (marker.value === "1") return;

    const existingNativeRefresh = await this.getRefresh();
    if (existingNativeRefresh) {
      // Already populated by a prior session — just mark and stop.
      await native.preferences.set({
        key: NATIVE_MIGRATION_MARKER_KEY,
        value: "1",
      });
      return;
    }

    try {
      if (typeof localStorage === "undefined") {
        await native.preferences.set({
          key: NATIVE_MIGRATION_MARKER_KEY,
          value: "1",
        });
        return;
      }
      const legacyRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (legacyRefresh) {
        await this.setRefresh(legacyRefresh);
      }
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch {
      // localStorage inaccessible — nothing to migrate.
    }
    await native.preferences.set({
      key: NATIVE_MIGRATION_MARKER_KEY,
      value: "1",
    });
  }
}

// ── Adapter factory ──────────────────────────────────────────────────

export const tokenStore: TokenStore = native.isNative
  ? new NativeTokenStore()
  : new WebTokenStore();

/** Test-only — reset the native in-memory access token. */
export function __resetNativeAccessTokenForTests(): void {
  nativeAccessTokenMemory = null;
}

export const TOKEN_STORE_KEYS = {
  ACCESS: ACCESS_TOKEN_KEY,
  REFRESH: REFRESH_TOKEN_KEY,
  NATIVE_MIGRATION_MARKER: NATIVE_MIGRATION_MARKER_KEY,
} as const;
